import * as React from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { axiosInstance } from '@/modules/shared/lib/axios'
import { TextField } from '@/ui/form/controls/TextField'
import { Button } from '@/ui/button'
import { FieldsetSwitch } from '@/modules/settings/components/FieldsetSwitch'
import { SettingsSectionWithTabs } from '@/modules/settings/components/SettingsSectionWithTabs'
import { usePersistentTab } from '@/modules/shared/stores/tabsStore'
import { showCookiePreferences } from '@/modules/legal'

type ProfileAccount = {
  id: number
  login: string
  firstName: string
  surname: string
  cardNumber: string | null
  companyAdmin: boolean
  demoCompanyAdmin: boolean
  driverEntityId: number | null
  applyToAllCompanies: boolean
}

type ProfileFormState = {
  firstName: string
  surname: string
  cardNumber: string
  applyToAllCompanies: boolean
}

type EditUserProfilePayload = {
  id: number
  login: string
  firstname: string
  surname: string
  pesel: string
  cardNumber: string
  company: string
  applyToAllCompanies: boolean
}

type EditUserPayload = {
  id: number
  companyId: string | null
  login: string
  firstName: string
  surname: string
  phone: string
  companyAdmin: boolean
  demoCompanyAdmin: boolean
  companyRoles: never[]
  groupRoles: never[]
  driverEntityId: number | null
  applyToAllCompanies: boolean
}

async function editUserProfile(payload: EditUserProfilePayload) {
  const { data } = await axiosInstance.post(
    '/config-api/user/editUserProfile',
    payload,
  )
  return data
}

async function editUser(payload: EditUserPayload) {
  const { data } = await axiosInstance.post(
    '/config-api/user/editUser',
    payload,
  )
  return data
}

export const MyProfilePage: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>()
  const queryClient = useQueryClient()

  const { data: account, isLoading } = useQuery<ProfileAccount>({
    queryKey: ['config.user.profile', companyId],
    queryFn: async () => {
      const { data } = await axiosInstance.get(`/config-api/user`, {
        params: companyId ? { companyId } : undefined,
      })
      return data
    },
    enabled: !!companyId,
  })

  const [form, setForm] = React.useState<ProfileFormState>({
    firstName: '',
    surname: '',
    cardNumber: '',
    applyToAllCompanies: false,
  })
  const [feedback, setFeedback] = React.useState<{
    kind: 'success' | 'error'
    text: string
  } | null>(null)

  const [selectedTab, setSelectedTab] = usePersistentTab('profile', 'a')

  React.useEffect(() => {
    if (account) {
      setForm((prev) => ({
        ...prev,
        firstName: account.firstName ?? '',
        surname: account.surname ?? '',
        cardNumber: account.cardNumber ?? '',
        applyToAllCompanies: account.applyToAllCompanies ?? false,
      }))
    }
  }, [account])

  const onSuccess = () => {
    setFeedback({ kind: 'success', text: 'Profil zaktualizowany.' })
    queryClient.invalidateQueries({ queryKey: ['config.user.profile'] })
    queryClient.invalidateQueries({ queryKey: ['config.user'] })
  }

  React.useEffect(() => {
    if (feedback?.kind !== 'success') return undefined
    const timeout = window.setTimeout(() => setFeedback(null), 4000)
    return () => window.clearTimeout(timeout)
  }, [feedback])

  const onError = (err: any) => {
    const text =
      err?.response?.data?.message ||
      err?.message ||
      'Nie udało się zapisać profilu.'
    setFeedback({ kind: 'error', text })
  }

  const profileMutation = useMutation({
    mutationFn: editUserProfile,
    onSuccess,
    onError,
  })
  const globalMutation = useMutation({
    mutationFn: editUser,
    onSuccess,
    onError,
  })

  const onChange =
    (field: 'firstName' | 'surname' | 'cardNumber') =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }))
    }

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!account) return

    setFeedback(null)

    if (form.applyToAllCompanies) {
  
      globalMutation.mutate({
        id: account.id,
        companyId: null,
        login: account.login,
        firstName: form.firstName.trim(),
        surname: form.surname.trim(),
        phone: '',
        companyAdmin: account.companyAdmin,
        demoCompanyAdmin: account.demoCompanyAdmin,
        companyRoles: [],
        groupRoles: [],
        driverEntityId: account.driverEntityId,
        applyToAllCompanies: form.applyToAllCompanies,
      })
      return
    }

    if (!companyId) {
      setFeedback({
        kind: 'error',
        text: 'Brak kontekstu firmy. Wybierz firmę i spróbuj ponownie.',
      })
      return
    }

    profileMutation.mutate({
      id: account.id,
      login: account.login,
      firstname: form.firstName.trim(),
      surname: form.surname.trim(),
      pesel: '',
      cardNumber: form.cardNumber.trim(),
      company: companyId,
      applyToAllCompanies: form.applyToAllCompanies,
    })
  }

  const isPending = profileMutation.isPending || globalMutation.isPending

  if (isLoading || !account) {
    return (
      <div className="tw-flex tw-flex-1 tw-items-center tw-px-6 tw-py-8 tw-text-gray-600">
        Ładowanie profilu…
      </div>
    )
  }

  return (
    <form
      onSubmit={onSubmit}
      className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col"
    >
      <div className="tw-flex tw-min-h-0 tw-flex-1 tw-overflow-y-auto">
        <SettingsSectionWithTabs
          className="tw-mb-2"
          selected={selectedTab}
          setSelected={setSelectedTab}
        >
          <div
            data-tab-name="a"
            title="Dane osobowe"
            className="tw-flex tw-max-w-[428px] tw-flex-col tw-gap-6 tw-p-2 tw-pb-8"
          >
            <TextField
              label="Imię"
              value={form.firstName}
              onChange={onChange('firstName')}
              fullWidth
            />
            <TextField
              label="Nazwisko"
              value={form.surname}
              onChange={onChange('surname')}
              fullWidth
            />
            <TextField
              label="Numer karty kierowcy"
              value={form.cardNumber}
              onChange={onChange('cardNumber')}
              fullWidth
              helper="Opcjonalne. Numer karty kierowcy używany przy imporcie danych."
            />
            <TextField
              label="Login / e-mail"
              value={account.login}
              disabled
              readOnly
              fullWidth
              helper="Login zarządzany przez system uwierzytelniania — nie można go tu zmienić."
            />
          </div>

          <div
            data-tab-name="b"
            title="Prywatność"
            className="tw-flex tw-max-w-[428px] tw-flex-col tw-gap-6 tw-p-2 tw-pb-8"
          >
            <FieldsetSwitch
              label="Zastosuj do wszystkich moich firm"
              checked={form.applyToAllCompanies}
              name="applyToAllCompanies"
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  applyToAllCompanies: e.target.checked,
                }))
              }
            >
              <span className="tw-mt-1 tw-block tw-text-xs tw-text-gray-600">
                Imię i nazwisko zostaną zaktualizowane we wszystkich firmach, do
                których masz dostęp. Wyłączone — zmiany dotyczą tylko bieżącej
                firmy. Numer karty zawsze dotyczy bieżącej firmy.
              </span>
            </FieldsetSwitch>

            <div className="tw-h-[1px] tw-w-full tw-bg-base-300" />

            <div className="tw-flex tw-flex-col tw-gap-2">
              <label className="tw-mb-0 tw-px-1 tw-pb-0.5 tw-text-sm tw-font-medium">
                Pliki cookie
              </label>
              <p className="tw-px-1 tw-text-sm tw-text-gray-600">
                Zarządzaj zgodami na pliki cookie. Możesz w każdej chwili włączyć
                lub wyłączyć poszczególne kategorie.
              </p>
              <div className="tw-flex tw-justify-start tw-px-1 tw-pt-1">
                <Button
                  type="button"
                  size="sm"
                  variant="tertiary"
                  onClick={showCookiePreferences}
                  text="Otwórz ustawienia plików cookie"
                />
              </div>
            </div>
          </div>
        </SettingsSectionWithTabs>
      </div>

      <footer className="tw-flex tw-items-center tw-justify-end tw-gap-4 tw-border-t tw-border-base-300 tw-bg-base-100 tw-px-6 tw-py-3">
        {feedback && (
          <span
            role="status"
            className={
              feedback.kind === 'success'
                ? 'tw-text-sm tw-text-green-700'
                : 'tw-text-sm tw-text-red-700'
            }
          >
            {feedback.text}
          </span>
        )}
        <Button
          type="submit"
          size="sm"
          loading={isPending}
          text={isPending ? 'Zapisywanie…' : 'Zapisz zmiany'}
        />
      </footer>
    </form>
  )
}
